import { ToastProvider } from "@uzverse/ui";
import { useTranslation } from "react-i18next";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { BrandHead } from "./brand/BrandHead.js";
import { useCommonT } from "./components/common.js";
import { Layout, useDocumentLanguage } from "./components/Layout.js";
import { AdvancedSettingsPage } from "./pages/AdvancedSettingsPage.js";
import { ApprovalsPage } from "./pages/ApprovalsPage.js";
import { ChatPage } from "./pages/ChatPage.js";
import { ChangePasswordPage } from "./pages/ChangePasswordPage.js";
import { CompaniesPage } from "./pages/CompaniesPage.js";
import { CreditsPage } from "./pages/CreditsPage.js";
import { InvitePage } from "./pages/InvitePage.js";
import { LoginPage } from "./pages/LoginPage.js";
import { MemoryPage } from "./pages/MemoryPage.js";
import { OnboardingPage } from "./pages/OnboardingPage.js";
import { SettingsPage } from "./pages/SettingsPage.js";
import { TasksPage } from "./pages/TasksPage.js";
import { WorldPage } from "./pages/WorldPage.js";
import { AssistantProvider } from "./state/assistant.js";
import { useSession } from "./state/session.js";

function RequireSession() {
  const { ready, user, connection, retryConnection } = useSession();
  const { t } = useTranslation("web");
  const location = useLocation();
  if (!ready) {
    if (connection === "ok") return null;
    return (
      <p role="status" className="connection-notice" data-testid="connection-notice">
        {connection === "reconnecting" ? t("connection.reconnecting") : t("connection.failed")}{" "}
        {connection === "failed" && (
          <button type="button" onClick={retryConnection}>
            {t("connection.retry")}
          </button>
        )}
      </p>
    );
  }
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  // The password was set by someone else: nothing else is reachable until it is changed.
  if (user.mustChangePassword) return <ChangePasswordPage />;
  return (
    <AssistantProvider>
      <BrandHead />
      <Outlet />
    </AssistantProvider>
  );
}

/**
 * Route table. Signed-in screens share the `Layout`; `/world` hosts the 3D client mount point;
 * `/welcome` is the first-run onboarding (assistant name and voice).
 */
export function App() {
  const { t } = useTranslation("web");
  const tc = useCommonT();
  useDocumentLanguage();
  return (
    <ToastProvider t={tc}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/invite/:inviteToken" element={<InvitePage />} />
        <Route element={<RequireSession />}>
          <Route path="welcome" element={<OnboardingPage />} />
          <Route element={<Layout />}>
            <Route index element={<ChatPage />} />
            <Route path="tasks" element={<TasksPage />} />
            <Route path="approvals" element={<ApprovalsPage />} />
            <Route path="memory" element={<MemoryPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="settings/advanced" element={<AdvancedSettingsPage />} />
            <Route path="world" element={<WorldPage />} />
            <Route path="companies" element={<CompaniesPage />} />
            <Route path="credits" element={<CreditsPage />} />
            <Route path="*" element={<p>{t("notFound")}</p>} />
          </Route>
        </Route>
      </Routes>
    </ToastProvider>
  );
}
